import exifr from 'exifr';

export interface ImageLocation {
  latitude: number;
  longitude: number;
  placeName?: string;
}

/**
 * Extract GPS coordinates from an image file's EXIF metadata
 */
export async function extractImageLocation(file: File): Promise<ImageLocation | null> {
  try {
    const gps = await exifr.gps(file);
    if (!gps?.latitude || !gps?.longitude) return null;
    
    return {
      latitude: gps.latitude,
      longitude: gps.longitude,
    };
  } catch {
    // EXIF parsing failed (unsupported format, no metadata, etc.)
    return null;
  }
}

/**
 * Convert coordinates to a human-readable place name using OpenStreetMap Nominatim
 * This is a free API but has rate limits - use sparingly
 */
export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=10`,
      {
        headers: {
          'User-Agent': 'Seyu App (https://seyu.lovable.app)',
        },
      }
    );
    
    if (!response.ok) return null;
    
    const data = await response.json();
    
    // Build a nice place name from the address components
    const address = data.address;
    if (!address) return null;
    
    // Prefer city/town + country format
    const locality = address.city || address.town || address.village || address.municipality;
    const country = address.country;
    
    if (locality && country) {
      return `${locality}, ${country}`;
    }
    
    // Fallback to state/region + country
    const region = address.state || address.region || address.county;
    if (region && country) {
      return `${region}, ${country}`;
    }
    
    // Last resort: just the country
    if (country) {
      return country;
    }
    
    return null;
  } catch {
    return null;
  }
}

/**
 * Extract location from image and optionally reverse geocode it
 * Returns coordinates immediately, resolves place name in background
 */
export async function extractAndGeocodeLocation(
  file: File,
  onPlaceNameResolved?: (placeName: string) => void
): Promise<ImageLocation | null> {
  const location = await extractImageLocation(file);
  if (!location) return null;
  
  // Fire off reverse geocoding in background if callback provided
  if (onPlaceNameResolved) {
    reverseGeocode(location.latitude, location.longitude).then((placeName) => {
      if (placeName) {
        onPlaceNameResolved(placeName);
      }
    });
  }
  
  return location;
}
